import { Overlay } from '@angular/cdk/overlay';
import { AsyncPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { firstValueFrom, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { SnapshotService } from '@features/budget/services/snapshot.service';
import { SnapshotModalComponent } from '@features/snapshot-modal/snapshot-modal.component';

import { BudgetStore } from '@models/budget/budget.store';

import { IconComponent } from '@shared/components/icon/icon.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { VarDirective } from '@shared/directives/var.directive';
import { BudgetSnapshotType, EventType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventQuery } from '@shared/store/event/event.query';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    VarDirective,
    NgSelectComponent,
    FormsModule,
    ReactiveFormsModule,
    NgOptionTemplateDirective,
    TooltipDirective,
    IconComponent,
    AsyncPipe,
    DatePipe,
  ],
  selector: 'aux-compare-dropdown',
  styles: [
    `
      ::ng-deep .compare-dropdown .ng-option-selected::after {
        display: none;
      }
    `,
  ],
  templateUrl: './compare-dropdown.component.html',
})
export class CompareDropdownComponent implements OnInit {
  public overlay = inject(Overlay);

  private budgetStore = inject(BudgetStore);
  private readonly destroyRef = inject(DestroyRef);
  private eventQuery = inject(EventQuery);
  private overlayService = inject(OverlayService);
  private snapshotService = inject(SnapshotService);

  BUDGET_SNAPSHOT_USER_CREATED = BudgetSnapshotType.BUDGET_SNAPSHOT_USER_CREATED;
  @Input() disabled?: boolean;
  formControl = new UntypedFormControl();
  @Input() initialValue?: string;
  loading$ = this.snapshotService.loading$;
  @Input() refreshTable!: VoidFunction;
  snapshots$ = this.snapshotService.getSnapShotVersions();
  @Output() valueChange = new EventEmitter<string>();

  ngOnInit(): void {
    this.eventQuery
      .selectProcessingEvent$(EventType.CREATE_TRIAL_BUDGET_SNAPSHOT)
      .subscribe((isProcessing) => {
        if (isProcessing === false) {
          this.overlayService.showFieldTooltip(
            'compare-snapshot-dropdown',
            'Snapshot successfully created'
          );
          void firstValueFrom(this.snapshotService.getSnapshotList());
        }
      });
    this.formControl.setValue(this.initialValue);

    this.snapshots$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((options) => {
          const isValueExist = options.find(({ value }) => value === this.formControl.value);

          if (!isValueExist) {
            this.formControl.setValue(undefined);
          }
        })
      )
      .subscribe();

    this.loading$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((loading) => {
      if (loading || this.disabled) {
        this.formControl.disable();
      } else {
        this.formControl.enable();
      }
    });

    this.formControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((snapshotName) => {
          this.valueChange.emit(snapshotName);

          if (!snapshotName) {
            return of();
          }

          return this.snapshotService.getBudgetSnapshots(snapshotName);
        })
      )
      .subscribe();
  }

  deleteSnapshot(
    event: Event,
    snapshot: {
      create_date: string;
      id: string;
      label: string;
      snapshot_type: string;
      value: string;
    }
  ) {
    event.stopPropagation();

    this.overlayService.openPopup<
      { message: string },
      boolean | undefined,
      ConfirmationModalComponent
    >({
      content: ConfirmationModalComponent,
      data: {
        message: `Are you sure you want to remove ${snapshot.label} plan?`,
      },
      settings: {
        header: 'Remove Plan',
        primaryButton: {
          action: async (instance) => {
            instance?.ref.close();
            await this.emitUpdatedValue(
              snapshot.label,
              undefined,
              () => this.snapshotService.removeBudgetSnapshots(snapshot),
              () => {
                if (this.formControl.value === snapshot.label) {
                  this.snapshotService.setOriginalBudgetData();
                }
              }
            );
          },
          label: 'Remove',
        },
      },
    });
  }

  async editSnapshot(
    event: Event,
    snapshot: {
      create_date: string;
      id: string;
      label: string;
      snapshot_type: string;
      value: string;
    }
  ) {
    event.stopPropagation();
    const name = snapshot.label;
    const response = await firstValueFrom(
      this.overlayService.openPopup<{ name?: string }, { name?: string }>({
        data: { name },
        modal: SnapshotModalComponent,
        settings: { header: 'Name Vendor Budget Snapshot' },
      }).afterClosed$
    );

    if (response.data?.name) {
      const n = response.data.name;

      await this.emitUpdatedValue(
        name,
        n,
        () => {
          return this.snapshotService.updateSnapshots(snapshot, n);
        },
        () => this.formControl.setValue(n)
      );
    }
  }

  private async emitUpdatedValue(
    value: string,
    newName: string | undefined,
    callback: () => Promise<void>,
    updateControlCallback?: VoidFunction
  ) {
    if (value === this.formControl.value) {
      this.valueChange.emit(newName);
      this.budgetStore.setLoading(true);

      if (updateControlCallback) {
        updateControlCallback();
      }

      await callback();
      this.refreshTable();
    } else {
      await callback();
    }

    this.budgetStore.setLoading(false);
  }

  groupByFn(option: {
    create_date: string;
    id: string;
    label: string;
    snapshot_type: string;
    value: string;
  }) {
    switch (option.snapshot_type) {
      case BudgetSnapshotType.BUDGET_SNAPSHOT_USER_CREATED:
        return 'MANUAL SNAPSHOTS';
      default:
        return 'AUTOMATIC SNAPSHOTS ';
    }
  }
}
